import {useEffect, useState} from 'react'
import {createUseStyles} from 'react-jss'
import {useNavigate, useParams} from "react-router-dom";
import Switch from '../components/Switch'
import {Colors} from '../constants/colors'
import Button from '../components/Button'
import CardSection from '../components/CardSection'
import DetailsCard from '../components/DetailsCard'
import {
  CreateIntegrationSigningCredentialModel,
  CreateNewPayoutCreditFacilityModel,
  CustomerPortalUser,
  IntegrationSigningCredentialModel,
  UpdateCustomerModel,
  UpdateCustomerPortalUserModel,
} from '../redux/types'
import ConfirmModal from '../components/ConfirmModal'
import ApiKeyModal from '../components/ApiKeyModal'
import {useAppDispatch, useAppSelector} from '../redux/hooks';
import FullScreenLoadingIndicator from '../components/FullScreenLoadingIndicator';
import Divider from '../components/Divider';
import {faArrowLeft, faExclamationTriangle} from "@fortawesome/free-solid-svg-icons";
import NavLink from '../components/NavLink';
import useProtectCustomerActions from '../hooks/useProtectCustomerActions';
import {titleMarginBottom} from '../constants/layout';
import useCustomerActions from '../hooks/useCustomerActions';
import EditCustomerModal from '../components/EditCustomerModal';
import useEmployerActions from '../hooks/useEmployerActions';
import Link from '../components/Link';
import useDidUpdateEffect from '../hooks/useDidUpdateEffect';
import { employerCreationHandled } from '../redux/slices/employerSlice';
import usePayoutCreditFacilityActions from '../hooks/usePayoutCreditFacilityActions';
import { payoutCreditFacilityCreationHandled } from '../redux/slices/payoutCreditFacilitySlice';
import CustomerPortalUserList from '../components/CustomerPortalUser/CustomerPortalUserList';
import EditCustomerPortalUserModal from '../components/CustomerPortalUser/EditCustomerPortalUserModal';
import StateBadge from '../components/StateBadge';
import useIntegrationSigningCredentialActions from '../hooks/useIntegrationSigningCredentialActions';
import AddIntegrationSigningCredentialModal from '../components/IntegrationSigningCredentials/AddIntegrationSigningCredentialModal';
import IntegrationSigningCredentialList from '../components/IntegrationSigningCredentials/IntegrationSigningCredentialList';
import AddPayoutCreditFacilityModal from '../components/PayoutCreditFacility/AddPayoutCreditFacilityModal';

const CustomerDetails = () => {
  const styles = useStyles()
  const params = useParams<{id: string}>()
  const navigate = useNavigate();
  const dispatch = useAppDispatch()

  const [confirmLockCustomerModalVisible, setConfirmLockCustomerModalVisible] = useState(false)
  const [confirmCreateEmployerModalVisible, setConfirmCreateEmployerModalVisible] = useState(false)
  const [addPayoutCreditFacilityModalVisible, setAddPayoutCreditFacilityModalVisible] = useState(false)
  const [addSigningCredentialModalVisible, setAddSigningCredentialModalVisible] = useState(false)
  const [confirmRevokeSigningCredentialModalVisible, setConfirmRevokeSigningCredentialModalVisible] = useState(false)
  const [apiKeyModalVisible, setApiKeyModalVisible] = useState(false)
  const [updateCustomerModalVisible, setUpdateCustomerModalVisible] = useState(false)
  const [editedPortalUser, setEditedPortalUser] = useState<CustomerPortalUser | null>(null)
  const [portalUserDeleteCandidate, setPortalUserDeleteCandidate] = useState<CustomerPortalUser | null>(null)
  const [addPortalUserModalVisible, setAddPortalUserModalVisible] = useState(false)
  const [editPortalUserModalVisible, setEditPortalUserModalVisible] = useState(false)
  const [confirmDeletePortalUserModalVisible, setConfirmDeletePortalUserModalVisible] = useState(false)
  const [confirmTerminateCustomerModalVisible, setConfirmTerminateCustomerModalVisible] = useState(false)
  const [confirmUnterminateCustomerModalVisible, setConfirmUnterminateCustomerModalVisible] = useState(false)
  const [signingCredentialRevokeCandidate, setSigningCredentialRevokeCandidate] = useState<IntegrationSigningCredentialModel | null>(null)

  const loading = useAppSelector(state => state.customer.loading)
  const customer = useAppSelector(state => state.customer.customerDetails)
  const protectCustomer = useAppSelector(state => state.protectCustomer.customerDetails)
  const createdEmployerId = useAppSelector(state => state.employer.createdEmployerId)
  const createdPayoutCreditFacilityId = useAppSelector(state => state.payoutCreditFacility.createdFacilityId)
  const companiesWithUnhandledChanges = useAppSelector(state => state.companyMonitor.companiesWithUnhandledChanges)

  const {
    getCustomerDetails,
    setLockState,
    updateCustomer,
    addPortalUser,
    updatePortalUser,
    deletePortalUser,
    terminate,
    unterminate,
  } = useCustomerActions()

  const { createEmployer } = useEmployerActions()
  const { createPayoutCreditFacility } = usePayoutCreditFacilityActions()
  const { getProtectCustomer } = useProtectCustomerActions()
  const {
    credentials,
    loading: signingCredentialLoading,
    getCredentials,
    createCredential,
    revokeCredential
  } = useIntegrationSigningCredentialActions()

  useEffect(() => {
    if ((customer?.id === null || customer?.id !== params.id) && params.id) {
      getCustomerDetails(params.id)
    }
  }, [])

  useEffect(() => {
    if ((protectCustomer === null || protectCustomer.id !== params.id) && params.id) {
      getProtectCustomer(params.id)
    }
  }, [])

  useDidUpdateEffect(() => {
    if (createdEmployerId) {
      navigate(`/employers/${createdEmployerId}`)
      dispatch(employerCreationHandled())
    }
  }, [createdEmployerId])

  useDidUpdateEffect(() => {
    if (createdPayoutCreditFacilityId) {
      navigate(`/payoutcreditfacilities/${createdPayoutCreditFacilityId}`)
      dispatch(payoutCreditFacilityCreationHandled())
    }
  }, [createdPayoutCreditFacilityId])

  useEffect(() => {
    if (customer?.id) {
      getCredentials(customer.id)
    }
  }, [customer?.id])

  const handleLockStateChange = () => {
    if (customer?.state === 'active') {
      setConfirmLockCustomerModalVisible(true)
    } else if (customer?.state === 'locked') {
      setLockState(customer?.id as string, false)
    }
  }

  const handleLockCustomerConfirmed = () => {
    setLockState(customer?.id as string, true)
    setConfirmLockCustomerModalVisible(false)
  }

  const handleTerminateCustomerConfirmed = () => {
    terminate(customer?.id as string)
    setConfirmTerminateCustomerModalVisible(false)
  }

  const handleUnterminateCustomerConfirmed = () => {
    unterminate(customer?.id as string)
    setConfirmUnterminateCustomerModalVisible(false)
  }

  const handleCreateEmployerConfirmed = () => {
    if (customer) {
      createEmployer({customerId: customer.id})
    }
    setConfirmCreateEmployerModalVisible(false)
  }

  const handleCreatePayoutCreditFacility = (facility: CreateNewPayoutCreditFacilityModel) => {
    createPayoutCreditFacility(facility)
    setAddPayoutCreditFacilityModalVisible(false)
  }

  const handleCustomerUpdated = (updateModel: UpdateCustomerModel) => {
    updateCustomer(customer?.id as string, updateModel)
    setUpdateCustomerModalVisible(false)
  }

  const handleEditPortalUserClicked = (portalUser: CustomerPortalUser) => {
    setEditedPortalUser(portalUser)
    setEditPortalUserModalVisible(true)
  }

  const handleDeletePortalUserClicked = (portalUser: CustomerPortalUser) => {
    setPortalUserDeleteCandidate(portalUser)
    setConfirmDeletePortalUserModalVisible(true)
  }

  const handleDeletePortalUserConfirmed = () => {
    deletePortalUser(customer?.id as string, portalUserDeleteCandidate?.portalUserId as string)
    setConfirmDeletePortalUserModalVisible(false)
  }

  const handlePortalUserEdited = (portalUser: UpdateCustomerPortalUserModel) => {
    if (!!customer && !!editedPortalUser) {
      updatePortalUser(customer.id, editedPortalUser.portalUserId, portalUser)
      setEditedPortalUser(null)
    }
    setEditPortalUserModalVisible(false)
  }

  const handlePortalUserAdded = (portalUser: UpdateCustomerPortalUserModel) => {
    if (!!customer) {
      addPortalUser(customer.id, portalUser)
    }
    setAddPortalUserModalVisible(false)
  }

  const handleSigningCredentialAdded = (credential: CreateIntegrationSigningCredentialModel) => {
    if (customer) {
      createCredential(customer.id, credential)
    }
    setAddSigningCredentialModalVisible(false)
  }

  const handleRevokeSigningCredentialClicked = (credential: IntegrationSigningCredentialModel) => {
    setSigningCredentialRevokeCandidate(credential)
    setConfirmRevokeSigningCredentialModalVisible(true)
  }

  const handleRevokeSigningCredentialConfirmed = () => {
    if (customer && signingCredentialRevokeCandidate) {
      revokeCredential(customer.id, signingCredentialRevokeCandidate.id)
    }
    setSigningCredentialRevokeCandidate(null)
    setConfirmRevokeSigningCredentialModalVisible(false)
  }

  const renderCompanyInformationCardBody = () => {
    return (
      <div>
        <p>Market: <span>{customer?.marketName}</span></p>
        <p>Registration number: <span>{customer?.companyRegistrationNumber}</span></p>
        <p>Legal name: <span>{customer?.legalName}</span></p>
        <p>Display name: <span>{customer?.name}</span></p>
        <p>State: <span>{customer?.state}</span></p>
        <p>Agreement number: <span>{customer?.agreementNumber}</span></p>
        <p>Industry: <span>{customer?.industry}</span></p>
        <p>Invoice address: <span>{customer?.invoiceEmailAddress}</span></p>
        <p>Bank: <span>{customer?.bankName}</span></p>
        <p>Bank account type: <span>{customer?.bankAccountType}</span></p>
        <p>Bank account: <span>{customer?.bankAccountNumber}</span></p>
        <p>Bic: <span>{customer?.bic}</span></p>
        <p>Language: <span>{customer?.language}</span></p>
        <p>Time zone: <span>{customer?.timeZoneId}</span></p>
        <p>Portal auth scheme: <span>{customer?.defaultPortalUserAuthenticationScheme}</span></p>
      </div>
    )
  }

  const renderLockedSwitch = () => {
    const isLocked = customer?.state === 'locked'
    return (
      <div className={styles.lockSwitchContainer}>
        <Switch
          id='lockSwitch'
          key='lockSwitch'
          checked={isLocked}
          label={isLocked ? 'Manual lock on' : 'Manual lock off'}
          disabled={customer?.state === 'terminated'}
          onChange={() => handleLockStateChange()} />
      </div>
    )
  }

  const renderTerminationButton = () => {
    const isTerminated = customer?.state === 'terminated'
    const canTerminate = customer?.state === 'locked'

    return (
      <Button
        title={isTerminated ? 'Unterminate' : 'Terminate'}
        disabled={!isTerminated && !canTerminate}
        onClick={() => isTerminated
          ? setConfirmUnterminateCustomerModalVisible(true)
          : setConfirmTerminateCustomerModalVisible(true)} />
    )
  }

  const renderMonitoringLink = () => {
    const showWarning = companiesWithUnhandledChanges.find(x => x.marketId === customer?.marketId && x.companyRegistrationNumber === customer.companyRegistrationNumber) !== undefined

    return <NavLink
      icon={ showWarning ? faExclamationTriangle : undefined }
      iconColor={Colors.yellow}
      title='Monitoring'
      onClick={() => navigate(`/companymonitor/${customer?.marketId}/${customer?.companyRegistrationNumber}`)} />
  }

  const renderProtectLink = () => {
    const showWarning = (protectCustomer?.issues.filter(x => !x.closed).length ?? 0) > 0

    return <NavLink
      icon={ showWarning ? faExclamationTriangle : undefined }
      iconColor={ Colors.red }
      title='Protect'
      onClick={() => navigate(`/protect/customers/${params.id}`)} />
  }

  return (
    <div className={styles.container}>
      <div className={styles.titleContainer}>
        <h1>{customer?.legalName}</h1>
        <StateBadge state={customer?.state} />
      </div>
      <div className={styles.actionBar}>
        <div className={styles.actionBarCell} style={{justifyContent: 'flex-start'}}>
          <NavLink title='Back' icon={faArrowLeft} onClick={() => navigate(-1)} />
        </div>
        <div className={styles.actionBarCell} style={{justifyContent: 'center'}}>
          <div className={styles.navLinkContainer}>
            { renderMonitoringLink() }
            { renderProtectLink() }
          </div>
        </div>
        <div className={styles.actionBarCell} style={{justifyContent: 'flex-end'}}>

              { renderLockedSwitch() }
              { renderTerminationButton() }

            <Button
              title='Generate API key'
              onClick={() => setApiKeyModalVisible(true)} />
        </div>

      </div>
      <CardSection>
        <DetailsCard onClick={() => setUpdateCustomerModalVisible(true)}>
          <h6>General information</h6>
          {renderCompanyInformationCardBody()}
        </DetailsCard>
        <DetailsCard iconType='Add' onClick={customer?.employer != null ? undefined : () => setConfirmCreateEmployerModalVisible(true)}>
          <h6>Employer</h6>
          {customer?.employer &&
            <div>
              <p>Employer: <Link to={`/employers/${customer?.employer?.id}`}>{customer?.name}</Link></p>
              <p>State: <span>{customer?.employer?.state}</span></p>
            </div>
          }
          {!customer?.employer &&
            <div>
              <p>Customer has no employer configured</p>
            </div>
          }
        </DetailsCard>
        <DetailsCard iconType='Add' onClick={customer?.payoutCreditFacility != null ? undefined : () => setAddPayoutCreditFacilityModalVisible(true)}>
          <h6>Payout credit facility</h6>
          {customer?.payoutCreditFacility &&
            <div>
              <p>Payout credit facility: <Link to={`/payoutcreditfacilities/${customer?.payoutCreditFacility?.id}`}>{customer?.name}</Link></p>
              <p>State: <span>{customer?.payoutCreditFacility?.state}</span></p>
            </div>
          }
          {!customer?.payoutCreditFacility &&
            <div>
              <p>Customer has no payout credit facility configured</p>
            </div>
          }
        </DetailsCard>
      </CardSection>
      <CardSection>
        <DetailsCard iconType='Add' onClick={() => setAddPortalUserModalVisible(true)}>
            <h6>Portal users</h6>
            <CustomerPortalUserList
              portalUsers={customer?.portalUsers ?? []}
              onEditClick={handleEditPortalUserClicked}
              onDeleteClick={handleDeletePortalUserClicked}
            />
        </DetailsCard>
      </CardSection>
      <CardSection>
        <DetailsCard iconType='Add' onClick={() => setAddSigningCredentialModalVisible(true)}>
          <h6>Signing credentials</h6>
          <IntegrationSigningCredentialList
            credentials={credentials}
            onRevokeClick={handleRevokeSigningCredentialClicked}
          />
        </DetailsCard>
      </CardSection>

      <Divider height={50}/>
      <ConfirmModal
        visible={confirmLockCustomerModalVisible}
        confirmText={`Are you sure you want to lock this customer?`}
        onConfirmClick={handleLockCustomerConfirmed}
        onCancelClick={() => setConfirmLockCustomerModalVisible(false)}
      />
      <ConfirmModal
        visible={confirmTerminateCustomerModalVisible}
        confirmText={`Are you sure you want to terminate this customer?`}
        okButtonTitle='Terminate'
        onConfirmClick={handleTerminateCustomerConfirmed}
        onCancelClick={() => setConfirmTerminateCustomerModalVisible(false)}
      />
      <ConfirmModal
        visible={confirmUnterminateCustomerModalVisible}
        confirmText={`Are you sure you want to unterminate this customer?`}
        okButtonTitle='Unterminate'
        onConfirmClick={handleUnterminateCustomerConfirmed}
        onCancelClick={() => setConfirmUnterminateCustomerModalVisible(false)}
      />
      <ConfirmModal
        visible={confirmCreateEmployerModalVisible}
        confirmText={`Are you sure you want to create an employer for this customer?`}
        onConfirmClick={handleCreateEmployerConfirmed}
        onCancelClick={() => setConfirmCreateEmployerModalVisible(false)}
      />
      <ConfirmModal
        visible={confirmRevokeSigningCredentialModalVisible}
        confirmText={`Are you sure you want to revoke signing credential ${signingCredentialRevokeCandidate?.thumbprint ?? ''}?`}
        okButtonTitle='Revoke'
        onConfirmClick={handleRevokeSigningCredentialConfirmed}
        onCancelClick={() => setConfirmRevokeSigningCredentialModalVisible(false)}
      />
      <ConfirmModal
        visible={confirmDeletePortalUserModalVisible}
        confirmText={`Are you sure you want to remove portal user with email ${portalUserDeleteCandidate?.email}?`}
        onConfirmClick={handleDeletePortalUserConfirmed}
        onCancelClick={() => setConfirmDeletePortalUserModalVisible(false)}
      />
      {customer && <EditCustomerPortalUserModal
        key='addCustomerPortalUserModal'
        portalRoles={customer.portalRoles}
        onCancelClick={() => setAddPortalUserModalVisible(false)}
        onSaveClick={handlePortalUserAdded}
        visible={addPortalUserModalVisible}
      />}
      {customer && <EditCustomerPortalUserModal
        key='editCustomerPortalUserModal'
        initialValue={editedPortalUser}
        portalRoles={customer.portalRoles}
        onSaveClick={handlePortalUserEdited}
        onCancelClick={() => {
          setEditPortalUserModalVisible(false)
          setEditedPortalUser(null)}}
        visible={editPortalUserModalVisible}
      />}
      <ApiKeyModal
        visible={apiKeyModalVisible}
        customerId={customer?.id}
        onCloseClick={() => setApiKeyModalVisible(false)}
      />
      <EditCustomerModal
        visible={updateCustomerModalVisible}
        initialValue={customer ?? undefined}
        onCancelClick={() => setUpdateCustomerModalVisible(false)}
        onSaveClick={handleCustomerUpdated}
      />
      <AddIntegrationSigningCredentialModal
        visible={addSigningCredentialModalVisible}
        onCancelClick={() => setAddSigningCredentialModalVisible(false)}
        onAddClick={handleSigningCredentialAdded}
      />
      {customer &&
        <AddPayoutCreditFacilityModal
          visible={addPayoutCreditFacilityModalVisible}
          customerId={customer.id}
          onCancelClick={() => setAddPayoutCreditFacilityModalVisible(false)}
          onAddClick={handleCreatePayoutCreditFacility}
        />
      }
      <FullScreenLoadingIndicator visible={loading || signingCredentialLoading} />
    </div>
  )
}

export default CustomerDetails

const useStyles = createUseStyles({
  container: {
    '& h1': {
      marginBottom: titleMarginBottom
    },
    '& h5': {
      fontFamily: 'Aestetico-Regular',
      color: Colors.darkDefault,
    }
  },
  titleContainer: {
    display: 'flex',
    flexDirection: 'row',
    columnGap: 10,
    alignItems: 'center',
    marginBottom: titleMarginBottom,
    '& h1': {
      marginBottom: 0
    }
  },
  actionBar: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  actionBarCell: {
    display: 'flex',
    flexDirection: 'row',
    columnGap: 15,
    alignItems: 'center',
    width: '33%',
    marginBottom: -8
  },
  lockSwitchContainer: {
    marginBottom: 10
  },
  navLinkContainer: {
    display: 'flex',
    flexDirection: 'row',
    columnGap: 10
  },
  cardColumnContainer: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: 10
  },
  largeButton: {
    fontSize: "xxx-large",
    alignSelf: "center",
    margin: "20px",
    flexGrow: 1,
    transition: '0.4s',
    '&:hover': {
      color: Colors.euro5,
      cursor: 'pointer',
    }
  }
})
